// game/meta.ts — the banked side of the loop (DESIGN §7).
//
// Owns: the two wallets (Embers, Crests), the Ember Shrine tree, the
// Provisioner's stock, the Ledger's feat progress, lifetime stats, and the
// single object a run start consumes — runModifiers().
//
// The line: data/unlocks.ts and data/feats.ts are tables; every `effect` string
// in them resolves to an entry in EFFECTS below, exactly the way relic hooks
// resolve in game/relics.ts. Nothing in data executes.
//
// Meta is never lost on death. Embers bank in full, feats tick during losing
// runs, and a feat that completes mid-run pays its Crests immediately.

import type { FeatDef, SaveData, UnlockNode } from '../types'
import { load as loadSave, save as writeSave, wipe as wipeSave, freshSave } from '../engine/save'
import {
  PROVISIONER_STOCK,
  SHRINE_NODES,
  STOCK_GATED_RELICS,
  UNLOCK_BY_ID,
} from '../data/unlocks'
import { FEATS, FEATS_BY_TRACK, FEAT_BY_ID } from '../data/feats'
import { RELICS } from '../data/relics'

export interface RunSummary {
  zone: number
  zoneName: string
  roomsCleared: number
  kills: number
  embers: number
  gold: number
  timeMs: number
  died: boolean
  won: boolean
  /** Ended by the player from the pause menu — banked like any other end. */
  abandoned: boolean
}

/** A deed that paid out during the run just finished (summary line). */
export interface CrestEvent {
  featId: string
  name: string
  crests: number
}

/**
 * Everything the meta layer sells, collapsed into one object the run start
 * consumes. Base layer only: relic deltas apply on top of these numbers, which
 * is why game/relics.ts recomputes as deltas.
 */
export interface RunModifiers {
  maxHpAdd: number
  damageMul: number
  speedMul: number
  startPotions: number
  potionCap: number // added on top of TUNING.player.potionCap
  startGold: number
  emberMul: number
  dashChargeAdd: number
  reviveCharges: number
  regenPerSec: number
  vaultClockAdd: number
  curatorActive: boolean
  lampRadiusMul: number
  gloomRateMul: number
  altarDiscountMul: number
  tollbreaker: boolean
  secondWindHeal: number
  emberFontPerBoss: number
  foodHealAdd: number
  freeStartRelic: boolean
  specialCooldownMul: number
  // --- talismans
  wardingAbsorb: boolean
  pullImmune: boolean
  graveCandle: boolean
  goldMagnetRadius: number
  tollDiscountMul: number
  /** relic ids allowed in the in-run pool this run (Provisioner Stock I/II). */
  relicPool: string[]
}

export function freshModifiers(): RunModifiers {
  return {
    maxHpAdd: 0,
    damageMul: 1,
    speedMul: 1,
    startPotions: 0,
    potionCap: 0,
    startGold: 0,
    emberMul: 1,
    dashChargeAdd: 0,
    reviveCharges: 0,
    regenPerSec: 0,
    vaultClockAdd: 0,
    curatorActive: false,
    lampRadiusMul: 1,
    gloomRateMul: 1,
    altarDiscountMul: 1,
    tollbreaker: false,
    secondWindHeal: 0,
    emberFontPerBoss: 0,
    foodHealAdd: 0,
    freeStartRelic: false,
    specialCooldownMul: 1,
    wardingAbsorb: false,
    pullImmune: false,
    graveCandle: false,
    goldMagnetRadius: 0,
    tollDiscountMul: 1,
    relicPool: [],
  }
}

type Params = Record<string, number | string>

function num(p: Params | undefined, key: string, dflt: number): number {
  const v = p?.[key]
  return typeof v === 'number' && Number.isFinite(v) ? v : dflt
}

/**
 * The meta-effect registry: `UnlockNode.effect` -> what it does to a run.
 * `rank` is 1-based and already validated. This is the only place unlock data
 * turns into behaviour.
 */
const EFFECTS: Record<string, (m: RunModifiers, p: Params | undefined, rank: number) => void> = {
  maxHpAdd: (m, p, r) => {
    m.maxHpAdd += num(p, 'add', 10) * r
  },
  damageMul: (m, p, r) => {
    m.damageMul += num(p, 'perRank', 0.06) * r
  },
  speedMul: (m, p, r) => {
    m.speedMul += num(p, 'perRank', 0.04) * r
  },
  startPotions: (m, p, r) => {
    m.startPotions += num(p, 'add', 1) * r
  },
  potionCapAdd: (m, p, r) => {
    m.potionCap += num(p, 'add', 1) * r
  },
  startGold: (m, p, r) => {
    m.startGold += num(p, 'add', 60) * r
  },
  emberMul: (m, p, r) => {
    m.emberMul += num(p, 'add', 0.15) * r
  },
  secondWind: (m, p, r) => {
    m.secondWindHeal += num(p, 'heal', 20) * r
  },
  dashChargeAdd: (m, p, r) => {
    m.dashChargeAdd += num(p, 'add', 1) * r
  },
  revive: (m, p, r) => {
    m.reviveCharges += num(p, 'charges', 1) * r
  },
  regen: (m, p, r) => {
    m.regenPerSec += num(p, 'perSec', 1) * r
  },
  vaultClockAdd: (m, p, r) => {
    m.vaultClockAdd += num(p, 'seconds', 20) * r
  },
  curator: (m) => {
    m.curatorActive = true
  },
  lampRadiusMul: (m, p) => {
    m.lampRadiusMul *= num(p, 'mul', 1.25)
  },
  gloomRateMul: (m, p) => {
    m.gloomRateMul *= num(p, 'mul', 0.85)
  },
  specialCooldownMul: (m, p) => {
    m.specialCooldownMul *= num(p, 'mul', 0.85)
  },
  tollbreaker: (m) => {
    m.tollbreaker = true
  },
  altarDiscountMul: (m, p) => {
    m.altarDiscountMul *= num(p, 'mul', 0.8)
  },
  emberFontPerBoss: (m, p, r) => {
    m.emberFontPerBoss += num(p, 'add', 25) * r
  },
  foodHealAdd: (m, p, r) => {
    m.foodHealAdd += num(p, 'add', 10) * r
  },
  freeStartRelic: (m) => {
    m.freeStartRelic = true
  },
  ascendant: (m, p) => {
    m.maxHpAdd += num(p, 'hp', 10)
    m.damageMul += num(p, 'dmg', 0.05)
    m.potionCap += num(p, 'potionCap', 1)
  },
  // --- provisioner: weapons are chosen at the rack, not folded in here
  weapon: () => {},
  relicPool: () => {},
  // --- talismans (applied only when equipped)
  talEmberCharm: (m, p) => {
    m.emberMul += num(p, 'add', 0.1)
  },
  talWardingKnot: (m) => {
    m.wardingAbsorb = true
  },
  talTideAnchor: (m) => {
    m.pullImmune = true
  },
  talGraveCandle: (m) => {
    m.graveCandle = true
  },
  talCoinFeather: (m, p) => {
    m.goldMagnetRadius = Math.max(m.goldMagnetRadius, num(p, 'radius', 210))
    m.tollDiscountMul *= num(p, 'tollMul', 0.75)
  },
}

/** Relic ids that are in the pool before any Provisioner stock is bought. */
const BASE_RELIC_POOL: string[] = RELICS.map((r) => r.id).filter(
  (id) => STOCK_GATED_RELICS.indexOf(id) < 0,
)

export type BuyVerdict = 'ok' | 'locked' | 'maxed' | 'poor' | 'owned' | 'gated' | 'unknown'

class Meta {
  data: SaveData = freshSave()

  // in-run counters (never trusted by the save until banked)
  runEmbers = 0
  runKills = 0
  runRoomsCleared = 0
  runStartMs = 0
  lastSummary: RunSummary | null = null

  /** Deeds that paid during the run just finished — the summary reads these. */
  runCrests: CrestEvent[] = []
  /** featId -> progress gained during the run just finished. */
  runFeatGain: Record<string, number> = {}

  boot(): void {
    this.data = loadSave()
  }

  persist(): void {
    writeSave(this.data)
  }

  /** Pause-menu options: clamp, apply nothing here (callers own the engines), persist. */
  setSettings(volume: number, shake: number): void {
    this.data.settings.volume = Math.max(0, Math.min(1, volume))
    this.data.settings.shake = Math.max(0, Math.min(1, shake))
    this.persist()
  }

  reset(): void {
    this.data = wipeSave()
    this.runEmbers = 0
    this.runKills = 0
    this.runRoomsCleared = 0
    this.lastSummary = null
    this.runCrests.length = 0
    for (const k in this.runFeatGain) delete this.runFeatGain[k]
  }

  beginRun(): void {
    this.runEmbers = 0
    this.runKills = 0
    this.runRoomsCleared = 0
    this.runStartMs = Date.now()
    this.runCrests.length = 0
    for (const k in this.runFeatGain) delete this.runFeatGain[k]
    this.data.stats.runs++
    this.persist()
  }

  addEmbers(n: number): void {
    if (n <= 0) return
    this.runEmbers += n
  }

  /** `defId` feeds the family kill-count feats; absent is still a kill. */
  addKill(defId?: string): void {
    this.runKills++
    if (defId) this.featTick('kill:' + defId)
  }

  roomCleared(): void {
    this.runRoomsCleared++
  }

  // ------------------------------------------------------------- the Ledger

  /**
   * Route a counter tick to every feat listening on `track`. Progress is
   * persisted (it survives the losing run that earned it); a feat that reaches
   * its target pays its Crests on the spot and queues a summary line.
   */
  featTick(track: string, n = 1): void {
    if (!track || n <= 0) return
    const feats = FEATS_BY_TRACK.get(track)
    if (!feats) return
    let paid = false
    for (let i = 0; i < feats.length; i++) {
      const f = feats[i]
      if (this.data.featsClaimed.indexOf(f.id) >= 0) continue
      const before = this.data.feats[f.id] ?? 0
      const after = Math.min(f.target, before + n)
      if (after === before) continue
      this.data.feats[f.id] = after
      this.runFeatGain[f.id] = (this.runFeatGain[f.id] ?? 0) + (after - before)
      if (after >= f.target) {
        this.data.featsClaimed.push(f.id)
        this.data.crests += f.crests
        this.runCrests.push({ featId: f.id, name: f.name, crests: f.crests })
        paid = true
      }
    }
    // Crests are real the moment they are earned, even if the tab dies next.
    if (paid) this.persist()
  }

  featProgress(featId: string): number {
    return this.data.feats[featId] ?? 0
  }

  featDone(featId: string): boolean {
    return this.data.featsClaimed.indexOf(featId) >= 0
  }

  /** Hidden feats stay '???' until half done, then appear already half-full. */
  featRevealed(f: FeatDef): boolean {
    if (!f.hidden) return true
    if (this.featDone(f.id)) return true
    return this.featProgress(f.id) >= f.target * 0.5
  }

  feats(): FeatDef[] {
    return FEATS
  }

  reachedZone(z: number): void {
    if (z > this.data.zonesReached) this.data.zonesReached = z
    if (z > this.data.stats.bestZone) this.data.stats.bestZone = z
    this.featTick('reachZone' + z)
  }

  // ------------------------------------------------------------ the Shrine

  shrineRank(nodeId: string): number {
    const n = this.data.shrine[nodeId]
    return typeof n === 'number' && n > 0 ? Math.floor(n) : 0
  }

  /** Row 1 is open from the first visit; row N unveils on first reaching zone N. */
  rowOpen(row: number): boolean {
    return row <= 1 || this.data.zonesReached >= row
  }

  /** Cost of the next rank, or -1 when the node is maxed. */
  nextCost(node: UnlockNode): number {
    const rank = this.shrineRank(node.id)
    if (rank >= node.ranks) return -1
    return node.costs[Math.min(rank, node.costs.length - 1)]
  }

  canBuyShrine(nodeId: string): BuyVerdict {
    const node = UNLOCK_BY_ID.get(nodeId)
    if (!node || node.system !== 'shrine') return 'unknown'
    if (!this.rowOpen(node.row ?? 1)) return 'locked'
    const cost = this.nextCost(node)
    if (cost < 0) return 'maxed'
    if (this.data.embers < cost) return 'poor'
    return 'ok'
  }

  buyShrine(nodeId: string): boolean {
    if (this.canBuyShrine(nodeId) !== 'ok') return false
    const node = UNLOCK_BY_ID.get(nodeId)
    if (!node) return false
    const cost = this.nextCost(node)
    this.data.embers -= cost
    this.data.shrine[nodeId] = this.shrineRank(nodeId) + 1
    // Buying half of an either/or pair makes that half the active one unless the
    // partner is already owned — a purchase should never be silently inactive.
    const pairId = this.pairId(node)
    if (pairId) {
      const partner = node.pair ? UNLOCK_BY_ID.get(node.pair) : undefined
      if (!partner || this.shrineRank(partner.id) === 0) {
        this.data.shrineToggles[pairId] = this.side(node)
      }
    }
    this.persist()
    return true
  }

  pairId(node: UnlockNode): string {
    const v = node.params?.pairId
    return typeof v === 'string' ? v : ''
  }

  side(node: UnlockNode): 'a' | 'b' {
    return node.params?.side === 'b' ? 'b' : 'a'
  }

  /** Which half of a pair is live, honouring what is actually owned. */
  activeSide(pairId: string): 'a' | 'b' {
    const stored = this.data.shrineToggles[pairId]
    const sides = SHRINE_NODES.filter((n) => this.pairId(n) === pairId)
    const owned = sides.filter((n) => this.shrineRank(n.id) > 0)
    if (owned.length === 1) return this.side(owned[0])
    return stored === 'b' ? 'b' : 'a'
  }

  /** True when this node's bonus applies to a run (pairs: only the live half). */
  isNodeActive(node: UnlockNode): boolean {
    const pairId = this.pairId(node)
    if (!pairId) return true
    return this.activeSide(pairId) === this.side(node)
  }

  /** Free swap at the hub. Both halves keep their ranks. */
  setPairSide(pairId: string, side: 'a' | 'b'): void {
    if (!pairId) return
    this.data.shrineToggles[pairId] = side
    this.persist()
  }

  togglePair(pairId: string): void {
    this.setPairSide(pairId, this.activeSide(pairId) === 'a' ? 'b' : 'a')
  }

  // ------------------------------------------------------- the Provisioner

  ownsStock(id: string): boolean {
    return this.data.stock.indexOf(id) >= 0
  }

  /** Deed gates are visible, never secret: the Ledger entry that opens them. */
  deedGateMet(node: UnlockNode): boolean {
    return !node.deedGate || this.featDone(node.deedGate)
  }

  deedGateName(node: UnlockNode): string {
    if (!node.deedGate) return ''
    return FEAT_BY_ID.get(node.deedGate)?.name ?? node.deedGate
  }

  canBuyStock(id: string): BuyVerdict {
    const node = UNLOCK_BY_ID.get(id)
    if (!node || node.system !== 'provisioner') return 'unknown'
    if (this.ownsStock(id)) return 'owned'
    if (!this.deedGateMet(node)) return 'gated'
    if (this.data.crests < node.costs[0]) return 'poor'
    return 'ok'
  }

  buyStock(id: string): boolean {
    if (this.canBuyStock(id) !== 'ok') return false
    const node = UNLOCK_BY_ID.get(id)
    if (!node) return false
    this.data.crests -= node.costs[0]
    this.data.stock.push(id)
    // A bought talisman equips itself if the rack is empty — one less click
    // between the purchase and the thing it does.
    if (node.effect.startsWith('tal') && !this.data.loadout.talisman) {
      this.data.loadout.talisman = id
    }
    this.persist()
    return true
  }

  // ------------------------------------------------------------- the Rack

  ownedWeaponStock(): string[] {
    const out: string[] = []
    for (const n of PROVISIONER_STOCK) {
      if (n.effect !== 'weapon') continue
      if (!this.ownsStock(n.id)) continue
      const w = n.params?.weapon
      if (typeof w === 'string') out.push(w)
    }
    return out
  }

  setWeapon(weaponId: string): void {
    this.data.loadout.weapon = weaponId
    this.persist()
  }

  setTalisman(id: string | null): void {
    this.data.loadout.talisman = id
    this.persist()
  }

  // --------------------------------------------------------- run modifiers

  /**
   * The single object game.startRun() consumes. Computed fresh each call from
   * the save, so a hub purchase is live on the very next descent.
   */
  runModifiers(): RunModifiers {
    const m = freshModifiers()

    for (const node of SHRINE_NODES) {
      const rank = this.shrineRank(node.id)
      if (rank <= 0) continue
      if (!this.isNodeActive(node)) continue
      const fn = EFFECTS[node.effect]
      if (fn) fn(m, node.params, Math.min(rank, node.ranks))
    }

    const pool = BASE_RELIC_POOL.slice()
    for (const id of this.data.stock) {
      const node = UNLOCK_BY_ID.get(id)
      if (!node) continue
      if (node.effect === 'relicPool') {
        const list = String(node.params?.relics ?? '').split(',')
        for (const r of list) if (r && pool.indexOf(r) < 0) pool.push(r)
        continue
      }
      // Talismans: one equipped per run (DESIGN §7.2).
      if (node.effect.startsWith('tal') && this.data.loadout.talisman !== id) continue
      const fn = EFFECTS[node.effect]
      if (fn) fn(m, node.params, 1)
    }
    m.relicPool = pool

    return m
  }

  // --------------------------------------------------------------- banking

  /** Death or victory: embers bank in full, stats update, save is written once. */
  bankRun(s: RunSummary): RunSummary {
    this.data.embers += s.embers
    this.data.stats.embersLifetime += s.embers
    this.data.stats.kills += s.kills
    if (s.died) this.data.stats.deaths++
    // A win is the full descent. Finishing the deepest built zone is not one.
    if (s.won && s.zone >= 6) this.data.stats.wins++
    if (s.zone > this.data.stats.bestZone) this.data.stats.bestZone = s.zone
    if (s.won && s.zone >= 6 && (this.data.stats.bestTimeMs === 0 || s.timeMs < this.data.stats.bestTimeMs)) {
      this.data.stats.bestTimeMs = s.timeMs
    }
    if (s.won && s.zone >= 6 && s.timeMs < 28 * 60 * 1000) this.featTick('runUnder28')
    this.lastSummary = s
    this.persist()
    return s
  }

  // ---------------------------------------------------- summary read-backs

  /**
   * "Next: you can afford —" (DESIGN §8): the cheapest buyable shrine rank, or
   * the cheapest one still out of reach when nothing is affordable yet.
   */
  cheapestNode(affordableOnly: boolean): { node: UnlockNode; rank: number; cost: number } | null {
    let best: { node: UnlockNode; rank: number; cost: number } | null = null
    for (const node of SHRINE_NODES) {
      if (!this.rowOpen(node.row ?? 1)) continue
      const cost = this.nextCost(node)
      if (cost < 0) continue
      if (affordableOnly && cost > this.data.embers) continue
      if (!best || cost < best.cost) best = { node, rank: this.shrineRank(node.id) + 1, cost }
    }
    return best
  }

  // ------------------------------------------------------------- test seam

  /** Test-only injection (TestApi.grantMeta). */
  grant(patch: {
    embers?: number
    crests?: number
    zonesReached?: number
    shrineAll?: boolean
    stockAll?: boolean
  }): void {
    if (typeof patch.embers === 'number') this.data.embers = Math.max(0, patch.embers)
    if (typeof patch.crests === 'number') this.data.crests = Math.max(0, patch.crests)
    if (typeof patch.zonesReached === 'number') {
      this.data.zonesReached = Math.max(0, Math.min(6, patch.zonesReached))
      if (this.data.zonesReached > this.data.stats.bestZone) {
        this.data.stats.bestZone = this.data.zonesReached
      }
    }
    if (patch.shrineAll) {
      for (const node of SHRINE_NODES) this.data.shrine[node.id] = node.ranks
    }
    if (patch.stockAll) {
      for (const node of PROVISIONER_STOCK) {
        if (!this.ownsStock(node.id)) this.data.stock.push(node.id)
      }
    }
    this.persist()
  }
}

export const meta = new Meta()
